import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ApiService, CoveragePhaseService } from "../../../../api";
import {
	createNotification,
	CustomButton,
	CustomDialog,
	CustomIconButton,
	icon,
	SidePanel,
	SidePanelContent,
} from "../../../../common/components";
import { translate } from "../../../../common/providers";
import styles from "./ImportHeritagePanel.module.css";
import { useDeliverablesData } from "./hooks/useDeliverablesData";
import { useSaveLogic } from "./hooks/useSaveLogic";
import { useConfirmationDialog } from "./hooks/useConfirmationDialog";
import SaveActionIndicator from "./components/SaveActionIndicator";
import LeftPanel from "./components/LeftPanel";
import RightPanel from "./components/RightPanel";
import { updateImportsState, restoreCheckedStateFromImport } from "./utils/importStateUtils";

const ImportHeritagePanel = ({ open, onClose, phase, onImportSuccess }) => {
	const projectId = useSelector(({ context }) => context.project?.id);
	const [phases, setPhases] = useState([]);
	const [searchLeft, setSearchLeft] = useState("");
	const [searchRight, setSearchRight] = useState("");
	const [selectedSourcePhaseId, setSelectedSourcePhaseIdState] = useState(null);
	const [checkedRight, setCheckedRight] = useState({});
	const [selectedLeft, setSelectedLeft] = useState(null);
	const [leftPage, setLeftPage] = useState(0);
	const [rightPage, setRightPage] = useState(0);
	const [leftPerPage, setLeftPerPage] = useState(15);
	const [rightPerPage, setRightPerPage] = useState(15);
	const [imports, setImports] = useState([]);
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
	const pendingSelectedSourcePhaseIdRef = useRef(null);
	const skipConfirmRef = useRef(false);

	const {
		leftDeliverables,
		rightDeliverables,
		isLoadingLeft,
		isLoadingRight,
		leftTotalCount,
		rightTotalCount,
		cancelTokenRef,
		isMountedRef,
	} = useDeliverablesData({
		open,
		projectId,
		phaseId: phase?.id,
		selectedSourcePhaseId,
		searchLeft,
		searchRight,
		leftPage,
		leftPerPage,
		rightPage,
		rightPerPage,
	});

	const { saveState, setSaveState, hasUnsavedChanges, setHasUnsavedChanges, handleSave } = useSaveLogic({
		open,
		projectId,
		phaseId: phase?.id,
		imports,
		cancelTokenRef,
		isMountedRef,
	});

	const { confirmationDialog, showConfirmationDialog, closeConfirmationDialog, handleConfirmAction } =
		useConfirmationDialog();

	const searchFieldSx = {
		"& .MuiOutlinedInput-root": {
			height: "32px",
			minHeight: "32px",
			fontSize: "12px",
			borderRadius: "2px",
			backgroundColor: "var(--color-white)",
			"& input": {
				height: "32px",
				lineHeight: "32px",
				paddingTop: 0,
				paddingBottom: 0,
			},
		},
	};

	const handleApiError = (err) => {
		if (!ApiService.isRequestCancellation(err?.message)) {
			console.error(err);
		}
	};

	useEffect(() => {
		if (!open || !projectId || !phase?.id) {
			return undefined;
		}

		isMountedRef.current = true;
		if (!cancelTokenRef.current) {
			cancelTokenRef.current = ApiService.getCancelTokenSource();
		}

		CoveragePhaseService.getAllByProject({ projectId }, cancelTokenRef.current.token)
			.then((data) => {
				if (!isMountedRef.current) {
					return;
				}
				const items = data?.content || data?.contents || data || [];
				setPhases(items);
			})
			.catch(handleApiError);

		CoveragePhaseService.getImportStrategyDrafts({ projectId, phaseId: phase.id }, cancelTokenRef.current.token)
			.then((draftData) => {
				if (!isMountedRef.current) {
					return;
				}
				if (draftData.length > 0) {
					const draft = draftData[0];
					setSaveState("saved");
					setHasUnsavedChanges(false);
					setImports(draftData);

					setSelectedLeft(draft.sourceDeliverableId);
					skipConfirmRef.current = true;
					setSelectedSourcePhaseIdState(draft.sourcePhaseId || null);
					skipConfirmRef.current = false;

					const checkedState = {};
					if (Array.isArray(draft.targetDeliverableIds)) {
						draft.targetDeliverableIds.forEach((id) => {
							checkedState[id] = true;
						});
					}
					setCheckedRight(checkedState);
				}
			})
			.catch(handleApiError);

		return () => {
			isMountedRef.current = false;
			if (cancelTokenRef.current) {
				ApiService.cancelTokens(cancelTokenRef.current);
			}
		};
	}, [open, projectId, phase?.id, setSaveState, setHasUnsavedChanges, cancelTokenRef, isMountedRef]);

	useEffect(() => {
		if (!selectedLeft || !selectedSourcePhaseId || !phase?.id) {
			return;
		}

		setImports((prevImports) =>
			updateImportsState(prevImports, selectedLeft, selectedSourcePhaseId, phase.id, checkedRight)
		);
	}, [checkedRight, selectedLeft, selectedSourcePhaseId, phase?.id]);

	const toggleRight = (id) => {
		setCheckedRight((prev) => ({ ...prev, [id]: !prev[id] }));
	};

	const handleSelectAllRight = () => {
		const allSelected = rightDeliverables.every((d) => !!checkedRight[d.id]);
		if (allSelected) {
			setCheckedRight((prev) => {
				const copy = { ...prev };
				rightDeliverables.forEach((d) => delete copy[d.id]);
				return copy;
			});
		} else {
			setCheckedRight((prev) => {
				const copy = { ...prev };
				rightDeliverables.forEach((d) => (copy[d.id] = true));
				return copy;
			});
		}
	};

	const handleClose = () => {
		setCheckedRight({});
		setSelectedLeft(null);
		setSaveState("initial");
		setHasUnsavedChanges(false);
		setSelectedSourcePhaseIdState(null);
		setRightPage(0);
		setLeftPage(0);
		setLeftPerPage(15);
		setRightPerPage(15);
		setImports([]);
		onClose();
	};

	const handleValidate = () => {
		setConfirmDialogOpen(true);
	};

	const handleCloseConfirmDialog = () => setConfirmDialogOpen(false);

	const handleConfirmValidate = () => {
		CoveragePhaseService.saveImportStrategyDrafts(
			{
				projectId,
				phaseId: phase.id,
				importPayloads: imports,
			},
			cancelTokenRef.current.token
		).then((res) => {
			if (!isMountedRef.current) {
				return;
			}
			CoveragePhaseService.validateImportStrategies(
				{
					projectId,
					phaseId: phase.id,
					importPayloads: res,
				},
				cancelTokenRef.current.token
			)
				.then(() => {
					if (!isMountedRef.current) {
						return;
					}
					setConfirmDialogOpen(false);
					if (onImportSuccess) {
						onImportSuccess();
					}
					handleClose();
				})
				.catch((error) => {
					if (!isMountedRef.current) {
						return;
					}
					console.error("Validation error:", error);
					createNotification({
						type: "error",
						message: translate("project-phases.strategy-import.validation-error"),
					});
				});
		});
	};

	const handleSelectSourceDeliverable = (d) => {
		setSelectedLeft(d.id);
		setCheckedRight(restoreCheckedStateFromImport(imports, d.id));
	};

	const handleSourcePhaseChange = (newPhaseId) => {
		if (skipConfirmRef.current) {
			setSelectedSourcePhaseIdState(newPhaseId);
			return;
		}

		if (newPhaseId === selectedSourcePhaseId) {
			return;
		}

		if (!imports || imports.length === 0) {
			setSelectedSourcePhaseIdState(newPhaseId);
			return;
		}

		pendingSelectedSourcePhaseIdRef.current = newPhaseId;
		showConfirmationDialog("change-source-phase", () => {
			setImports([]);
			setCheckedRight({});
			setSelectedLeft(null);
			setHasUnsavedChanges(false);
			setSaveState("initial");

			setSelectedSourcePhaseIdState(pendingSelectedSourcePhaseIdRef.current);
			pendingSelectedSourcePhaseIdRef.current = null;
		});
	};

	return (
		<>
			<SidePanel open={open} size={55} onClose={handleClose}>
				<SidePanelContent
					secondaryActions={
						<SaveActionIndicator
							hasUnsavedChanges={hasUnsavedChanges}
							saveState={saveState}
							onSave={handleSave}
						/>
					}
					title={translate("project-phases.menu.import-heritage")}
					onClose={handleClose}
				>
					<div className={styles.contentWrapper}>
						<div className={styles.importContainer}>
							<LeftPanel
								isLoadingLeft={isLoadingLeft}
								leftDeliverables={leftDeliverables}
								leftPage={leftPage}
								leftPerPage={leftPerPage}
								leftTotalCount={leftTotalCount}
								phase={phase}
								phases={phases}
								searchFieldSx={searchFieldSx}
								searchLeft={searchLeft}
								selectedLeft={selectedLeft}
								selectedSourcePhaseId={selectedSourcePhaseId}
								onPageChange={setLeftPage}
								onRowsPerPageChange={setLeftPerPage}
								onSearchChange={setSearchLeft}
								onSelectDeliverable={handleSelectSourceDeliverable}
								onSourcePhaseChange={handleSourcePhaseChange}
							/>
							<div className={styles.arrow}>
								<CustomIconButton
									className={styles.arrowIcon}
									icon={icon.faArrowRight}
									iconColor="var(--color-white)"
									size="sm"
								/>
							</div>
							<RightPanel
								checkedRight={checkedRight}
								isLoadingRight={isLoadingRight}
								phase={phase}
								rightDeliverables={rightDeliverables}
								rightPage={rightPage}
								rightPerPage={rightPerPage}
								rightTotalCount={rightTotalCount}
								searchFieldSx={searchFieldSx}
								searchRight={searchRight}
								onPageChange={setRightPage}
								onRowsPerPageChange={setRightPerPage}
								onSearchChange={setSearchRight}
								onSelectAllRight={handleSelectAllRight}
								onToggleRight={toggleRight}
							/>
						</div>
					</div>
					<div className={styles.footer}>
						<CustomButton variant="outlined" onClick={handleClose}>
							{translate("project-phases.strategy-import.cancel")}
						</CustomButton>
						<CustomButton
							color="primary"
							disabled={!selectedSourcePhaseId || imports.length === 0}
							variant="contained"
							onClick={handleValidate}
						>
							{translate("project-phases.strategy-import.validate")}
						</CustomButton>
					</div>
				</SidePanelContent>
			</SidePanel>
			<CustomDialog
				cancelLabel={translate("project-phases.strategy-import.cancel")}
				iconColor="red"
				iconModel={icon.faExclamationTriangle}
				maxWidth="md"
				open={confirmDialogOpen}
				submitLabel={translate("project-phases.strategy-import.confirm")}
				subTitle={translate("project-phases.strategy-import.confirm.valid.body")}
				title={translate("project-phases.strategy-import.confirm.valid.title")}
				onClose={handleCloseConfirmDialog}
				onSubmit={handleConfirmValidate}
			/>
			<CustomDialog
				cancelLabel={translate("project-phases.strategy-import.cancel")}
				iconColor="red"
				iconModel={icon.faExclamationTriangle}
				open={confirmationDialog.open}
				submitLabel={translate("project-phases.strategy-import.confirm")}
				subTitle={confirmationDialog.message}
				title={confirmationDialog.title}
				onClose={closeConfirmationDialog}
				onSubmit={handleConfirmAction}
			/>
		</>
	);
};

export default ImportHeritagePanel;
